import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Upload } from 'lucide-react';

interface FormData {
  title: string;
  authors: string; // Ovo će biti konvertirano u string[] prije slanja
  institution: string;
  email: string;
  phone: string;
  file_url?: string;
  corresponding_author?: string;
  presentation_type?: 'presentation' | 'eposter';
  file_name?: string;
  file_size?: number;
}

export function PresentationUploadPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    authors: '',
    institution: '',
    email: '',
    phone: '',
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Provjera veličine
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('Datoteka je prevelika. Maksimalna veličina je 10MB.');
        return;
      }

      // Provjera tipa datoteke
      const allowedTypes = [
        'application/pdf',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Neispravan format datoteke. Dozvoljeni formati su: PDF, PPT, PPTX');
        return;
      }

      setSelectedFile(file);
    }
  };

  const uploadFile = async (file: File, recordId: string): Promise<string | null> => {
    try {
      // Pokušaj direktno uploadati u presentations bucket
      // Bucket mora biti kreiran u Supabase konzoli

      const fileExt = file.name.split('.').pop();
      const fileName = `presentation_${recordId}_${Date.now()}.${fileExt}`;
      
      // Pokušaj uploadati datoteku
      const { error } = await supabase.storage
        .from('presentations')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Error uploading file:', error);
        // Nastavi s izvršavanjem i vrati null ako ne uspije upload
        return null;
      }
      
      // Ako je upload uspješan, dohvati javni URL
      const { data: urlData } = await supabase.storage
        .from('presentations')
        .getPublicUrl(fileName);

      return urlData?.publicUrl || null;
    } catch (error) {
      console.error('Error in file upload:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validacija
      if (!selectedFile) {
        throw new Error('Molimo odaberite prezentaciju za upload.');
      }

      // Provjera da autor nije prazan
      if (!formData.authors.trim()) {
        toast.error('Molimo unesite ime i prezime autora');
        return;
      }

      // Odredimo tip prezentacije na temelju ekstenzije datoteke
      let presentationType: 'presentation' | 'eposter' = 'presentation';
      const fileExt = selectedFile.name.split('.').pop()?.toLowerCase();
      if (fileExt === 'pdf') {
        presentationType = 'presentation';
      } else if (fileExt === 'ppt' || fileExt === 'pptx') {
        presentationType = 'presentation';
      }

      // Prvo uploadamo datoteku
      const tempId = crypto.randomUUID(); // Generiramo privremeni ID za ime datoteke
      const fileUrl = await uploadFile(selectedFile, tempId);
      
      if (!fileUrl) {
        toast.error('Greška prilikom uploada datoteke. Molimo pokušajte ponovno.');
        return;
      }

      // Pripremimo podatke za slanje prema Presentation interfaceu
      // Escape specijalnih znakova u imenu autora
      const authorName = formData.authors.trim()
        .replace(/"/g, '\\"') // Escape navodnika
        .replace(/'/g, "''") // Escape apostrofa za SQL
        .replace(/\\/g, '\\\\'); // Escape backslasha
      
      const presentationData = {
        title: formData.title,
        authors: `{"${authorName}"}`, // PostgreSQL array format s navodnicima
        institution: formData.institution,
        email: formData.email,
        phone: formData.phone,
        corresponding_author: authorName,
        presentation_type: presentationType,
        file_name: selectedFile.name,
        file_size: selectedFile.size,
        file_url: fileUrl // Sada imamo URL prije inserta
      };

      console.log("Priprema podataka za slanje:", presentationData);

      // Sada insertiramo podatke s URL-om datoteke
      const { error: insertError } = await supabase
        .from('presentations')
        .insert([presentationData]);

      if (insertError) {
        console.error("Insert error:", insertError);
        if (insertError.code === '23505') {
          toast.error('Prezentacija s ovim naslovom već postoji.');
        } else if (insertError.code === '23503') {
          toast.error('Greška: Nevažeća referenca.');
        } else if (insertError.code === '42P01') {
          toast.error('Greška: Tablica nije pronađena.');
        } else {
          toast.error(`Greška prilikom spremanja podataka: ${insertError.message}`);
        }
        
        // Ako insert nije uspio, pokušajmo obrisati uploadanu datoteku
        const fileName = fileUrl.split('/').pop();
        if (fileName) {
          const { error: deleteError } = await supabase.storage
            .from('presentations')
            .remove([fileName]);
            
          if (deleteError) {
            console.error('Error deleting uploaded file after failed insert:', deleteError);
          }
        }
        
        return;
      }

      toast.success('Prezentacija je uspješno poslana!');
      navigate('/kongres');
    } catch (error: any) {
      console.error('Error:', error);
      
      // Detaljnije error poruke
      if (error?.message?.includes('storage')) {
        toast.error('Greška prilikom pristupa storage-u. Provjerite veličinu datoteke.');
      } else if (error?.message?.includes('permission')) {
        toast.error('Nemate dozvolu za upload prezentacije.');
      } else if (error?.message?.includes('network')) {
        toast.error('Mrežna greška. Provjerite internet vezu.');
      } else {
        toast.error('Došlo je do greške prilikom slanja prezentacije. Molimo pokušajte ponovno.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto px-4"
      >
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8">
            <h1 className="text-3xl font-bold text-center">Upload prezentacije</h1>
            <p className="text-blue-100 text-center mt-2">
              Molimo ispunite sve potrebne podatke i učitajte Vašu prezentaciju
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Title */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold text-gray-900">Naslov prezentacije</h2>
              <div>
                <input
                  type="text"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Unesite naslov prezentacije"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </motion.div>

            {/* Author Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold text-gray-900">Podaci o autoru</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ime i prezime autora
                  </label>
                  <input
                    type="text"
                    name="authors"
                    required
                    value={formData.authors}
                    onChange={handleInputChange}
                    placeholder="npr. Ivan Horvat"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Institucija
                  </label>
                  <input
                    type="text"
                    name="institution"
                    required
                    value={formData.institution}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold text-gray-900">Kontakt podaci</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </motion.div>

            {/* File Upload */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold text-gray-900">Upload prezentacije</h2>
              <div className="flex flex-col items-center justify-center w-full">
                <label
                  htmlFor="file-upload"
                  className="w-full flex flex-col items-center justify-center px-4 py-6 border-2 border-blue-300 border-dashed rounded-lg cursor-pointer bg-blue-50 hover:bg-blue-100 transition-colors duration-200"
                >
                  <Upload className="w-12 h-12 text-blue-500 mb-3" />
                  <div className="flex flex-col items-center">
                    <p className="mb-2 text-sm text-blue-500">
                      <span className="font-semibold">Kliknite za upload</span> ili povucite i ispustite
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, PPT ili PPTX (max. 10MB)
                    </p>
                  </div>
                  {selectedFile && (
                    <div className="mt-4 text-sm text-blue-600">
                      Odabrana datoteka: {selectedFile.name}
                    </div>
                  )}
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="hidden"
                    accept=".pdf,.ppt,.pptx"
                    onChange={handleFileChange}
                    required
                  />
                </label>
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="pt-6"
            >
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-4 px-8 text-lg font-bold text-white bg-blue-600 rounded-full 
                  ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'} 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                  transition-colors duration-200`}
              >
                {isSubmitting ? 'Slanje prezentacije...' : 'Pošalji prezentaciju'}
              </button>
            </motion.div>
          </form>
        </div>
      </motion.div>
    </div>
  );
} 